import { Heading, Text, Box, Flex, Image, Button, useBoolean, useDisclosure } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from 'utils/helpers';
import useGlobalStore from 'zStore';
import { format } from 'date-fns';
import { enUS, fr } from 'date-fns/locale';
import { PUBLIC_PAGES } from 'routes/pagePath';
import { baseURL, subRequest } from 'services';
import toast from 'react-hot-toast';
import axiosInstance from 'services/api';
import { useMutation } from '@tanstack/react-query';
import { nanoid } from 'nanoid';
import { useEffect, useMemo, useState } from 'react';
import PromoCodeBox from 'components/Subscription/PromoCodeBox';
import DiscountSuccessModal from 'components/Subscription/DiscountSuccessModal';
import LanguageSwitcher from 'components/LanguageSwitcher';
import { resolvePlanLogoSrc } from 'components/Subscription/resolvePlanLogo';
import { useTenant } from 'context/TenantContext';
import { useTranslation } from 'react-i18next';

const Order = () => {
  const { t, i18n } = useTranslation(['order', 'common', 'subscriptions']);
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const [selectedSub, discountCampaign] = useGlobalStore((state) => [state.subscription, state.discountCampaign]);
  const { date, amount, currency, image, id, businessId, durationType } = selectedSub;
  const logoSrc = resolvePlanLogoSrc(tenant.favicon, image || '');
  const dateLocale = (i18n.language || 'en').startsWith('fr') ? fr : enUS;
  const formattedDate = format(new Date(date), 'EEEE d MMM yyyy', { locale: dateLocale });
  const [isPaymentEnabled, setIsPaymentEnabled] = useBoolean();
  const subPrice = formatCurrency(amount, currency, i18n.language);
  const durationLabel = durationType
    ? t(`subscriptions:duration.${durationType}`)
    : selectedSub.duration;
  const durationWordLabel = durationType
    ? t(`subscriptions:durationWord.${durationType}`)
    : selectedSub.durationWord;
  const { 
    onClose: closeDiscountSuccessModal, 
    isOpen: isDiscountSuccessModalOpen, 
    onOpen: openDiscountSuccessModal 
  } = useDisclosure();

  useEffect(() => {
    if (businessId === '12345678') {
      const location = localStorage.getItem('current-location') || '/';
      window.location.href = location;
    }
  }, [navigate, businessId]);

  const onSuccess = (data: any) => {
    const paymentLink = data.data?.paymentLink;
    // eslint-disable-next-line
    if (paymentLink == null || paymentLink == 'null') {
      if (discountCampaign?.code && discountCampaign.value === 100) {
        openDiscountSuccessModal();
        return;
      }
      setIsPaymentEnabled.off();
      toast.error(t('order:paymentLinkError'));
    } else {
      window.location.href = paymentLink;
    }
  };

  const onError = (error: any) => {
    setIsPaymentEnabled.off();
    const err = error as Record<any, any>;
    const message: any = err?.response?.data?.errors;
    toast.dismiss();
    if (Array.isArray(message)) {
      message.map((errorMsg) => toast.error(`${errorMsg ?? t('common:errorOccurred')}`));
    } else {
      toast.error(`${message ?? t('common:errorOccurred')}`);
    }
  };
  const [xReqKey, setxReqKey] = useState('');

  useEffect(() => {
    const updateXReqKey = () => {
      const newId = nanoid();
      setxReqKey(newId);
    };

    updateXReqKey();

    const interval = setInterval(updateXReqKey, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const postRequest = async ({ url, data }: any) => {
    const response = await axiosInstance.post(url, data, {
      headers: {
        'X-Request-Ref': xReqKey,
      },
    });
    return response?.data || response;
  };

  const [autoRenew] = useBoolean(false);
  const { mutate, isLoading } = useMutation({
    mutationFn: postRequest,
    onSuccess,
    onError,
  });

  const getPaymentLink = async () => {
    const origin = window.location.origin;
    setIsPaymentEnabled.on();

    let payload = {
      businessId,
      subscriptionPlanId: id,
      recurrent: autoRenew,
      redirectUrl: `${origin}${PUBLIC_PAGES.PAYMENT_VERIFICATION}`,
    } as any;

    if (discountCampaign?.code) {
      payload = {
        ...payload,
        discountCode: discountCampaign.code,
      }
    }

    const data = JSON.stringify(payload);
    const url = `${baseURL}${discountCampaign?.code ? subRequest.INIT_DISCOUNT_PAYMENT : subRequest.INIT_PAYMENT}`;

    mutate({
      url,
      data,
    });
  };

  const calculateTotal = useMemo(() => {
    let total = amount;
    if (typeof discountCampaign?.discountAmount === 'number') {
      total = discountCampaign.discountAmount;
    }
    return formatCurrency(total, currency, i18n.language);
  }, [amount, currency, discountCampaign?.discountAmount, i18n.language])

  return (
    <Box p="24px" maxW="500px" mx="auto">
      <Flex justifyContent="space-between" alignItems="flex-start" gap="12px">
        <Box>
          <Heading fontSize="20px">{t('order:summary')}</Heading>
          <Text fontSize="14px">{t('order:confirmProceed')}</Text>
        </Box>
        <LanguageSwitcher variant="light" />
      </Flex>

      <Box
        p="16px"
        pl="12px"
        mt="32px"
        bg="#E7ECFD"
        border="1px solid #D0DAFB"
        borderRadius="8px"
        position="relative"
        overflow="hidden"
      >
        <Flex>
          {logoSrc ? (
            <Image
              w={{ base: '34px', md: '64px' }}
              h={{ base: '34px', md: '64px' }}
              mr="10px"
              borderRadius="8px"
              objectFit="cover"
              src={logoSrc}
              alt={t('order:planLogoAlt')}
            />
          ) : (
            <Box
              w={{ base: '34px', md: '64px' }}
              h={{ base: '34px', md: '64px' }}
              mr="10px"
              borderRadius="8px"
              bg="gray.200"
            />
          )}
          <Box>
            <Heading fontSize="16px" color="#161A5C">
              {t('order:planTitle', {
                tenantName: tenant.name,
                planName: selectedSub.planName,
                durationWord: durationWordLabel,
              })}
            </Heading>
            <Text mt="3px" w="90%" color="#45487D" fontSize="13px">
              {t('order:chargeNotice', {
                amount: formatCurrency(selectedSub.amount, selectedSub.currency, i18n.language),
                duration: durationLabel,
                date: formattedDate,
              })}
            </Text>
          </Box>
        </Flex>
        <Flex mt="25px" justifyContent="space-between">
          <Box />
          <Button
            variant="link"
            fontSize="13px"
            color="primary"
            textDecoration="underline"
            onClick={() => navigate(-1)}
          >
            {t('order:changePlan')}
          </Button>
        </Flex>
      </Box>

      <PromoCodeBox subscriptionPlanId={id} />
      <Flex
        mt="102px"
        background="#FBFCFE"
        padding="16px 12px"
        border="1px solid #E7ECFD"
        borderRadius="8px"
        gap="3"
        flexDir="column"
      >
        <Flex justifyContent="space-between">
          <Text fontSize="13px" color="#666666">
            {t('order:subscriptionPrice')}
          </Text>
          <Text fontSize="13px" color="#666666">
            {subPrice}
          </Text>
        </Flex>
        {discountCampaign ? (
          <Flex justifyContent="space-between">
            <Text fontSize="13px" color="#666666">
              {t('order:discount')}
            </Text>
            <Text fontSize="13px" color="#666666">
              - {formatCurrency(discountCampaign.pricingValue || 0, currency, i18n.language)}
            </Text>
          </Flex>
        ) : null}
        <Flex justifyContent="space-between">
          <Text fontSize="14px" fontWeight={700}>
            {t('order:total')}
          </Text>
          <Text fontSize="14px" fontWeight={700}>
            {calculateTotal}
          </Text>
        </Flex>
      </Flex>
      <Button
        mt="24px"
        w="100%"
        bg="primary"
        borderRadius="8px"
        h="48px"
        color="#fff"
        isLoading={isLoading}
        onClick={getPaymentLink}
        isDisabled={isPaymentEnabled}
      >
        {t('order:proceedToPayment')}
      </Button>
      <Button variant="secondaryOutline" mt="10px" h="48px" w="100%" onClick={() => navigate(-1)}>
        {t('common:cancel')}
      </Button>
      <DiscountSuccessModal isOpen={isDiscountSuccessModalOpen} onClose={closeDiscountSuccessModal} />
    </Box>
  );
};

export default Order;
